import { Grid } from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import AnalyticsMainPageContent from "./AnalyticsMainPageContent";
import axios from "axios";
import { API_URL } from "../../consts";

const TrendsAndAnalytics = () => {
  const [rates, setRates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAnalytics();
  }, []);

  const getAnalytics = () => {
    setIsLoading(true);
    // Real analytics are derived from the user's own ratings.
    // /rates/my-rates caps size at 50, so this is a "recent activity" snapshot.
    let url = API_URL + `/rates/my-rates`;
    axios
      .get(url, { params: { size: 50, start_from: 0 } })
      .then((res) => {
        setRates(res.data?.rates || []);
        setIsLoading(false);
      })
      .catch((err) => {
        // Non-auth failures: show the empty/error-free dashboard rather than fake data
        setRates([]);
        setIsLoading(false);
      });
  };

  // ----- Derive real metrics from the user's ratings -----
  const analytics = useMemo(() => {
    const totalRatings = rates.length;

    // Ratings over the last 6 months (line chart) grouped by month via create_time
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleString("en-US", { month: "short" }),
      });
    }
    const monthlyCounts = months.map(() => 0);
    rates.forEach((r) => {
      if (!r.create_time) return;
      const d = new Date(r.create_time);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const idx = months.findIndex((m) => m.key === key);
      if (idx >= 0) monthlyCounts[idx] += 1;
    });

    // Distribution of ratings by topic/tag (doughnut) - top tags, rest grouped as "Other"
    const tagCounts = {};
    rates.forEach((r) => {
      (r.tags || []).forEach((t) => {
        if (!t) return;
        tagCounts[t] = (tagCounts[t] || 0) + 1;
      });
    });
    const sortedTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]);
    const topTags = sortedTags.slice(0, 6);
    const otherTotal = sortedTags
      .slice(6)
      .reduce((sum, [, count]) => sum + count, 0);
    const tagLabels = topTags.map(([label]) => label);
    const tagValues = topTags.map(([, count]) => count);
    if (otherTotal > 0) {
      tagLabels.push("Other");
      tagValues.push(otherTotal);
    }

    // Distinct collections/spaces the user rated into
    const spaceIds = new Set();
    rates.forEach((r) => {
      (r.spaces_list || []).forEach((s) => {
        if (s && s.id) spaceIds.add(s.id);
      });
    });

    // Average star score across the user's rated items
    const starValues = rates
      .map((r) => Number(r.average_stars))
      .filter((v) => v > 0);
    const avgStars = starValues.length
      ? starValues.reduce((sum, v) => sum + v, 0) / starValues.length
      : 0;

    return {
      totalRatings,
      distinctTopics: Object.keys(tagCounts).length,
      distinctCollections: spaceIds.size,
      avgStars,
      monthLabels: months.map((m) => m.label),
      monthlyCounts,
      tagLabels,
      tagValues,
    };
  }, [rates]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        paddingTop: { xs: "16px", sm: "24px", md: "34px" },
        paddingX: { xs: "8px", sm: "16px" },
      }}
    >
      <Grid item xs={12}>
        <h2
          style={{
            fontSize: window.innerWidth < 600 ? "1.5rem" : "2rem",
            marginLeft: "8px",
          }}
        >
          Trends & Analytics
        </h2>
        <AnalyticsMainPageContent
          isLoading={isLoading}
          analytics={analytics}
        />
      </Grid>
    </Grid>
  );
};

export default TrendsAndAnalytics;
